// Pure snap-to-element helper. While dragging a placement, snap the dragged
// element's visual edges/center to the corresponding edges/center of every
// other placement, plus the canvas centerlines (x=50%, y=50%).
//
// `draggedBounds` and each entry of `otherBoundsList` are
// `{ left, centerX, right, top, centerY, bottom }` in canvas-percent.
//
// `thresholdPctX` / `thresholdPctY` are the snap thresholds in canvas-percent
// (caller computes them from a canvas-pixel threshold and the canvas
// dimensions, so the snap radius is constant in visible pixels).
//
// Returns `{ snappedX, guideX, deltaX, snappedY, guideY, deltaY }` where
//   - snappedX / snappedY: the % value the snapped edge moves to
//   - guideX / guideY: where to draw the magenta guide (== snappedX / snappedY)
//   - deltaX / deltaY: the adjustment to apply to the dragged placement's
//     `x` / `y` so the chosen edge lands on the snap target
// Each axis is independent. If no candidate falls within threshold on an axis,
// that axis's values are all `null`.
export const findSnapTargets = (draggedBounds, otherBoundsList, thresholdPctX, thresholdPctY) => {
  const xSides = ['left', 'centerX', 'right']
  const ySides = ['top', 'centerY', 'bottom']

  // Collect candidate values for each axis from the other placements, plus
  // canvas centerlines so a placement can snap to dead center even when no
  // other placements exist.
  const xCandidates = [50]
  const yCandidates = [50]
  for (const b of otherBoundsList) {
    xCandidates.push(b.left, b.centerX, b.right)
    yCandidates.push(b.top, b.centerY, b.bottom)
  }

  // For each axis, find (draggedSide, candidate) pair with smallest |delta|
  // within threshold.
  const findAxisSnap = (sides, candidates, threshold) => {
    let bestSide = null
    let bestCandidate = null
    let bestAbs = threshold
    for (const side of sides) {
      const sideValue = draggedBounds[side]
      for (const c of candidates) {
        const d = Math.abs(c - sideValue)
        if (d <= bestAbs) {
          bestAbs = d
          bestSide = side
          bestCandidate = c
        }
      }
    }
    if (bestSide === null) return { snapped: null, guide: null, delta: null }
    return {
      snapped: bestCandidate,
      guide: bestCandidate,
      delta: bestCandidate - draggedBounds[bestSide],
    }
  }

  const xRes = findAxisSnap(xSides, xCandidates, thresholdPctX)
  const yRes = findAxisSnap(ySides, yCandidates, thresholdPctY)

  return {
    snappedX: xRes.snapped,
    guideX: xRes.guide,
    deltaX: xRes.delta,
    snappedY: yRes.snapped,
    guideY: yRes.guide,
    deltaY: yRes.delta,
  }
}

// Smart-guide / equal-spacing detection. Figma-style: while dragging, detect
// when the dragged placement creates equal center-to-center spacing with the
// two nearest non-dragged placements on a given axis. When the dragged center
// is within `threshold` of the equal-spacing position, snap to it AND return
// data for rendering guide segments + a spacing pill.
//
// Pragmatic scope (simpler version): instead of enumerating every (A, B) pair,
// we sort the other placements' centers along the axis and consider only the
// two nearest neighbors to the dragged center. The dragged placement creates
// equal spacing if it sits at one of:
//   • centerNearest + (centerNearest - centerSecondNearest)   // outside-right
//   • centerNearest - (centerSecondNearest - centerNearest)   // outside-left
//   • midpoint(neighborA, neighborB)                          // between
// We test all three; the closest match within threshold wins.
//
// `draggedCenter` is the current canvas-percent center of the dragged element
// on this axis. `otherCenters` is an array of the other placements' centers on
// the same axis. `threshold` is in canvas-percent (same units).
//
// Returns:
//   null                              — no equal-spacing match
//   { snapped, delta, a, b, c, spacing }
//     snapped  — target center value (% on this axis)
//     delta    — snapped - draggedCenter (caller adds to current x or y)
//     a, b, c  — the three center values defining the equal-spacing chain,
//                sorted ascending. Caller uses these to render guide segments
//                between the three elements.
//     spacing  — |a - b| === |b - c| (% on this axis); caller converts back
//                to px for the spacing pill label.
export const findSmartGuide = (draggedCenter, otherCenters, threshold) => {
  if (otherCenters.length < 2) return null

  // Sort by distance from dragged center; take the two nearest neighbors.
  const sorted = [...otherCenters].sort(
    (p, q) => Math.abs(p - draggedCenter) - Math.abs(q - draggedCenter),
  )
  const n1 = sorted[0]
  const n2 = sorted[1]

  // Three candidate positions for the dragged element to create equal spacing
  // with (n1, n2). For each, the "chain" is the three sorted center values.
  const spacingAB = Math.abs(n1 - n2)
  const midAB = (n1 + n2) / 2

  // Candidate 1: dragged sits at the midpoint of n1 and n2 (between them).
  // The chain is [min(n1,n2), midAB, max(n1,n2)] with spacing = spacingAB / 2.
  const cand1 = {
    snapped: midAB,
    a: Math.min(n1, n2),
    b: midAB,
    c: Math.max(n1, n2),
    spacing: spacingAB / 2,
  }

  // Candidates 2 & 3: dragged is collinear-outside, equal-spacing of spacingAB.
  // Use whichever of n1/n2 is the "near" neighbor and the other as "far".
  // Outside-right of the larger: dragged = max + spacingAB.
  const maxOf = Math.max(n1, n2)
  const minOf = Math.min(n1, n2)
  const cand2 = {
    snapped: maxOf + spacingAB,
    a: minOf,
    b: maxOf,
    c: maxOf + spacingAB,
    spacing: spacingAB,
  }
  // Outside-left of the smaller: dragged = min - spacingAB.
  const cand3 = {
    snapped: minOf - spacingAB,
    a: minOf - spacingAB,
    b: minOf,
    c: maxOf,
    spacing: spacingAB,
  }

  // Pick the candidate whose `snapped` is closest to draggedCenter and within
  // threshold. Ignore degenerate spacings (== 0).
  let best = null
  let bestAbs = threshold
  for (const cand of [cand1, cand2, cand3]) {
    if (cand.spacing === 0) continue
    const d = Math.abs(cand.snapped - draggedCenter)
    if (d <= bestAbs) {
      bestAbs = d
      best = cand
    }
  }
  if (!best) return null
  return {
    snapped: best.snapped,
    delta: best.snapped - draggedCenter,
    a: best.a,
    b: best.b,
    c: best.c,
    spacing: best.spacing,
  }
}
